import React, { useEffect, useState } from "react";
import Switch from "@mui/joy/Switch";
import toast from "react-hot-toast";

import AddButton from "../../../../components/admin/usable/AddButton";
import CancelButton from "../../../../components/admin/usable/CancelButton";
import EditButton from "../../../../components/admin/usable/actions/EditButton";
import DeleteButton from "../../../../components/admin/usable/actions/DeleteButton";
import TableLoader from "../../../../components/admin/usable/TableLoader";

import {
  viewCity,
  addCity,
  updateCity,
  deleteCity,
} from "../../../../api/admin/cityApi";

import { viewCountry } from "../../../../api/admin/countryApi";
import { viewDivision } from "../../../../api/admin/divisionApi";

const City = () => {
  const [CityData, setCityData] = useState([]);
  const [CountryData, setCountryData] = useState([]);
  const [DivisionData, setDivisionData] = useState([]);

  const [Loading, setLoading] = useState(true);
  const [Submitting, setSubmitting] = useState(false);

  const [FormData, setFormData] = useState({
    id: null,
    cityName: "",
    countryId: "",
    divisionId: "",
    isFeatured: 0,
    status: 1,
  });

  useEffect(() => {
    viewCity(setCityData, setLoading);
    viewCountry(setCountryData);
    viewDivision(setDivisionData);
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;

    setFormData((prev) => {
      if (id === "countryId") {
        return {
          ...prev,
          countryId: value,
          divisionId: "",
        };
      }

      return {
        ...prev,
        [id]: value,
      };
    });
  };

  const handleFeaturedChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      isFeatured: event.target.checked ? 1 : 0,
    }));
  };

  const handleStatusChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      status: event.target.checked ? 1 : 0,
    }));
  };

  const resetForm = () => {
    setFormData({
      id: null,
      cityName: "",
      countryId: "",
      divisionId: "",
      isFeatured: 0,
      status: 1,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!FormData.cityName.trim()) {
      toast.error("City name is required");
      return;
    }

    if (!FormData.countryId) {
      toast.error("Please select a country");
      return;
    }

    if (!FormData.divisionId) {
      toast.error("Please select a division");
      return;
    }

    const payload = {
      ...FormData,
      cityName: FormData.cityName.trim(),
      countryId: Number(FormData.countryId),
      divisionId: Number(FormData.divisionId),
      isFeatured: Number(FormData.isFeatured),
      status: Number(FormData.status),
    };

    setSubmitting(true);

    try {
      if (FormData.id) {
        await updateCity(payload, setFormData, setCityData, setLoading);
      } else {
        await addCity(payload, setFormData, setCityData, setLoading);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      id: item.id,
      cityName: item.cityName || "",
      countryId: item.countryId ?? "",
      divisionId: item.divisionId ?? "",
      isFeatured: Number(item.isFeatured),
      status: Number(item.status),
    });
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this city?")) {
      deleteCity(id, setCityData, setLoading);
    }
  };

  const FilteredDivisions = DivisionData.filter(
    (item) => Number(item.countryId) === Number(FormData.countryId),
  );

  return (
    <div className="w-full bg-white border rounded-lg p-4">
      <div className="py-3">
        <p className="font-bold">City</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="cityName" className="block mb-2">
              City Name
            </label>

            <input
              type="text"
              id="cityName"
              value={FormData.cityName}
              onChange={handleChange}
              placeholder="Enter city name"
              className="w-full border rounded-md px-3 py-2 focus:outline-none"
            />
          </div>

          <div>
            <label htmlFor="countryId" className="block mb-2">
              Country
            </label>

            <select
              id="countryId"
              value={FormData.countryId}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2 focus:outline-none"
            >
              <option value="">Select Country</option>

              {CountryData.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.countryName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="divisionId" className="block mb-2">
              Division
            </label>

            <select
              id="divisionId"
              value={FormData.divisionId}
              onChange={handleChange}
              disabled={!FormData.countryId}
              className="w-full border rounded-md px-3 py-2 focus:outline-none disabled:bg-gray-100"
            >
              <option value="">Select Division</option>

              {FilteredDivisions.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.divisionName}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <div>
              <label className="block mb-2">Featured</label>

              <Switch
                checked={Number(FormData.isFeatured) === 1}
                onChange={handleFeaturedChange}
                slotProps={{
                  input: { "aria-label": "Featured city" },
                }}
              />
            </div>

            <span className="mt-6">
              {Number(FormData.isFeatured) === 1 ? "Featured" : "Not Featured"}
            </span>
          </div>

          <div>
            <label className="block mb-2">Status</label>

            <div className="flex items-center gap-2">
              <Switch
                checked={Number(FormData.status) === 1}
                onChange={handleStatusChange}
                slotProps={{
                  input: { "aria-label": "City status" },
                }}
              />

              <span>
                {Number(FormData.status) === 1 ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <AddButton
            type="submit"
            disabled={Submitting}
            text={Submitting ? "Submitting..." : FormData.id ? "Update" : "Add"}
          />

          <CancelButton onClick={resetForm} text={"Reset"} />
        </div>
      </form>

      <div className="overflow-x-auto pt-5">
        <table className="w-full border-collapse border">
          <thead>
            <tr>
              <th className="border p-2 text-center">SL</th>
              <th className="border p-2 text-center">City Name</th>
              <th className="border p-2 text-center">Country</th>
              <th className="border p-2 text-center">Division</th>
              <th className="border p-2 text-center">Featured</th>
              <th className="border p-2 text-center">Status</th>
              <th className="border p-2 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {Loading ? (
              <TableLoader colSpan={7} />
            ) : CityData.length > 0 ? (
              CityData.map((item, index) => (
                <tr key={item.id}>
                  <td className="border p-2 text-center">{index + 1}</td>

                  <td className="border p-2 text-center">{item.cityName}</td>

                  <td className="border p-2 text-center">
                    {item.countryName ||
                      CountryData.find(
                        (country) =>
                          Number(country.id) === Number(item.countryId),
                      )?.countryName ||
                      "N/A"}
                  </td>

                  <td className="border p-2 text-center">
                    {item.divisionName ||
                      DivisionData.find(
                        (division) =>
                          Number(division.id) === Number(item.divisionId),
                      )?.divisionName ||
                      "N/A"}
                  </td>

                  <td className="border p-2 text-center">
                    <span
                      className={`px-2 py-1 ${
                        Number(item.isFeatured) === 1
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-700"
                      } rounded text-xs`}
                    >
                      {Number(item.isFeatured) === 1
                        ? "featured"
                        : "not featured"}
                    </span>
                  </td>

                  <td className="border p-2 text-center">
                    <span
                      className={`px-2 py-1 ${
                        Number(item.status) === 1
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      } rounded text-xs`}
                    >
                      {Number(item.status) === 1 ? "active" : "inactive"}
                    </span>
                  </td>

                  <td className="border p-2 text-center">
                    <div className="flex gap-2 justify-center">
                      <EditButton onClick={() => handleEdit(item)} />
                      <DeleteButton onClick={() => handleDelete(item.id)} />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="text-center p-4">
                  No cities found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default City;
